import React from "react";
import { Link } from "react-router-dom";
import useTableStore, { ALL_TABLE_COLUMNS } from "../../../store/useTabStore";
import ImagePopover from "../../ui/popover";
import useClipboard from "../hooks/useClipboard";
import { TableResult } from "../types";
import ClipboardModal from "./ClipboardModal";
import {
  formatExperimentValue,
  formatValue,
  generateClipboardText,
  getCholesterolImagePath,
  getHelperLipidImagePath,
  getIonizableLipidImagePath,
  getLnpIdFormatted,
} from "../utils";

interface TableRowProps {
  result: TableResult;
  index: number;
}

const TableRow: React.FC<TableRowProps> = ({ result, index }) => {
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [clipboardText, setClipboardText] = React.useState("");

  const dynamicOptionColumns = ALL_TABLE_COLUMNS.filter(
    (col) =>
      ![
        "lnp_id",
        "library_code",
        "il",
        "hl",
        "chl",
        "peg",
        "il_molratio",
        "hl_molratio",
        "chl_molratio",
        "peg_molratio",
        "mass_ratio",
        "method",
        "value",
        "actions",
      ].includes(col.id),
  ).map((col) => col.id);

  const { setFilters, visibleColumns } = useTableStore();

  const handleExperimentIdClick = (experimentId: string) => {
    setFilters({ experiment_id: experimentId });
  };

  const { copySuccess: rowCopySuccess, copyToClipboard: rowCopyToClipboard } =
    useClipboard();

  const isVisible = (columnId: string) => visibleColumns.includes(columnId);

  const handleCopyClick = () => {
    const text = generateClipboardText(result);
    setClipboardText(text);
    rowCopyToClipboard(text);
    setIsModalOpen(true);
  };

  return (
    <tr
      key={index}
      className={`border-b hover:bg-gray-50 dark:hover:bg-gray-50 ${
        result.status === "draft" ? "bg-amber-50" : "bg-white"
      }`}
    >
      {isVisible("lnp_id") && (
        <td className="px-4 py-3 font-medium text-blue-600 whitespace-nowrap">
          <Link
            to={`/detail/${Number(result.index)}`}
            target="_blank"
            className="hover:underline"
          >
            {getLnpIdFormatted(result.index)}
          </Link>
        </td>
      )}

      {isVisible("library_code") && (
        <td className="px-4 py-3 font-medium text-blue-600 whitespace-nowrap">
          <button
            onClick={() => handleExperimentIdClick(result.experiment_id)}
            className="font-medium text-blue-600 hover:underline"
          >
            {result.experiment_id}
          </button>
        </td>
      )}

      {isVisible("il") && (
        <td className="px-4 py-3 transition-colors hover:bg-gray-100">
          <div className="relative">
            <ImagePopover
              imageData={getIonizableLipidImagePath(result.index)}
              alt={result.il_name}
            >
              {result.il_name}
            </ImagePopover>
          </div>
        </td>
      )}

      {isVisible("hl") && (
        <td className="px-4 py-3 transition-colors hover:bg-gray-100">
          <div className="relative">
            <ImagePopover
              imageData={getHelperLipidImagePath(result.hl_name)}
              alt={result.hl_name}
            >
              {result.hl_name}
            </ImagePopover>
          </div>
        </td>
      )}

      {isVisible("chl") && (
        <td className="px-4 py-3 transition-colors hover:bg-gray-100">
          <div className="relative">
            <ImagePopover
              imageData={getCholesterolImagePath(result.chl_name)}
              alt={result.chl_name}
            >
              {result.chl_name}
            </ImagePopover>
          </div>
        </td>
      )}

      {isVisible("peg") && <td className="px-4 py-3">{result.peg_name}</td>}

      {isVisible("il_molratio") && (
        <td className="px-4 py-3 font-medium text-center">
          {formatValue(result.il_molratio)}
        </td>
      )}
      {isVisible("hl_molratio") && (
        <td className="px-4 py-3 text-center">
          {formatValue(result.hl_molratio)}
        </td>
      )}
      {isVisible("chl_molratio") && (
        <td className="px-4 py-3 text-center">
          {formatValue(result.chl_molratio)}
        </td>
      )}
      {isVisible("peg_molratio") && (
        <td className="px-4 py-3 text-center">
          {formatValue(result.peg_molratio)}
        </td>
      )}

      {isVisible("mass_ratio") && (
        <td className="px-4 py-3 text-center">
          {formatValue(result.il_to_nucleicacid_massratio)}
        </td>
      )}

      {isVisible("method") && (
        <td className="px-4 py-3 text-center">
          {result.experiment_method?.replaceAll("_", " ")}
        </td>
      )}

      {isVisible("value") && (
        <td className="px-4 py-3 text-center">
          {formatExperimentValue(result.experiment_value)}
        </td>
      )}

      {dynamicOptionColumns.map(
        (columnId) =>
          isVisible(columnId) && (
            <td key={columnId} className="px-4 py-3 text-center">
              {formatValue(result[columnId as keyof TableResult])}
            </td>
          ),
      )}

      {isVisible("actions") && (
        <td className="px-4 py-3">
          <div className="flex items-center justify-center">
            <button
              onClick={handleCopyClick}
              className="relative inline-flex items-center justify-center p-1.5 text-gray-500 bg-gray-100 rounded-full hover:text-gray-900 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-300"
              title="Copy to MolCube"
            >
              <img
                src="/molcube_builder.png"
                alt="molcube_builder"
                width={24}
                className="rounded-full"
              />
              <span className="sr-only">Copy to MolCube</span>
            </button>
            {rowCopySuccess === "success" && (
              <span className="ml-2 text-xs text-green-500 animate-fade-in-out">
                ✓ Copied
              </span>
            )}
          </div>
        </td>
      )}
      <ClipboardModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        text={clipboardText}
        isBroadPharm={result.experiment_id?.includes("BroadPharm") || false}
        lnpId={getLnpIdFormatted(result.index)}
      />
    </tr>
  );
};

export default TableRow;
